import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  Euro, Users, Clock, Building2, Plus, Trash2, Loader2,
  ChevronDown, ChevronUp, Edit, Calendar, Wallet, TrendingUp,
  Receipt, Save, LogIn, LogOut
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const RegistroPaga = () => {
  const [operai, setOperai] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [timbrature, setTimbrature] = useState([]);
  const [acconti, setAcconti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedWorker, setExpandedWorker] = useState(null);
  const [expandedTimbrature, setExpandedTimbrature] = useState(null);
  const [expandedCantieri, setExpandedCantieri] = useState(null);
  const [selectedForPrint, setSelectedForPrint] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [showAddAcconto, setShowAddAcconto] = useState(null);
  const [accontoData, setAccontoData] = useState({ amount: '', note: '', date: '' });
  const [editingAcconto, setEditingAcconto] = useState(null);
  const [editAccontoData, setEditAccontoData] = useState({ amount: '', note: '', date: '' });
  const [editingTimb, setEditingTimb] = useState(null);
  const [editTimbData, setEditTimbData] = useState({ date: '', time: '', type: '' });
  const [addingTimb, setAddingTimb] = useState(null);
  const [newTimbData, setNewTimbData] = useState({ date: '', time: '', type: 'ingresso' });
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const [o, c, t, a] = await Promise.all([
        api.getOperai(), api.getCantieri(),
        api.getTimbratureRange(startDate, endDate),
        api.getAcconti(),
      ]);
      setOperai(o); setCantieri(c); setTimbrature(t); setAcconti(a);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [startDate, endDate]);

  const getInitials = (n) => n.split(' ').map(x => x[0]).join('').toUpperCase().slice(0, 3);
  const getCantiereName = (cId) => cantieri.find(c => c.id === cId)?.name || 'N/D';

  // Calculate hours for a worker in the date range
  const calcWorkerData = (workerId) => {
    const wt = timbrature
      .filter(t => t.workerId === workerId)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    let totalMinutes = 0;
    let lastIn = null;
    let lastInCantiere = '';
    const cantiereHours = {};
    const dailyHours = {};

    for (const t of wt) {
      if (t.type === 'ingresso') {
        lastIn = new Date(t.timestamp);
        lastInCantiere = t.cantiereId;
      } else if (t.type === 'uscita' && lastIn) {
        const mins = (new Date(t.timestamp) - lastIn) / 60000;
        totalMinutes += mins;
        // Per cantiere
        const cName = t.cantiereName || getCantiereName(lastInCantiere);
        cantiereHours[cName] = (cantiereHours[cName] || 0) + mins / 60;
        // Per day
        const day = t.timestamp.split('T')[0];
        dailyHours[day] = (dailyHours[day] || 0) + mins / 60;
        lastIn = null;
      }
    }
    // If still clocked in
    if (lastIn) {
      const mins = (new Date() - lastIn) / 60000;
      totalMinutes += mins;
      const cName = getCantiereName(lastInCantiere);
      cantiereHours[cName] = (cantiereHours[cName] || 0) + mins / 60;
      const day = new Date().toISOString().split('T')[0];
      dailyHours[day] = (dailyHours[day] || 0) + mins / 60;
    }

    const totalHours = totalMinutes / 60;
    return { totalHours, cantiereHours, dailyHours };
  };

  const getWorkerAcconti = (workerId) => acconti.filter(a => a.workerId === workerId);
  const getTotalAcconti = (workerId) => getWorkerAcconti(workerId).reduce((sum, a) => sum + a.amount, 0);

  const handleSaveRate = async (workerId) => {
    const isBusta = workerId.includes('_busta');
    const realId = isBusta ? workerId.replace('_busta', '') : workerId;
    const value = parseFloat(newRate) || 0;
    
    try {
      if (isBusta) {
        await api.updateOperaio(realId, { bustaPaga: value });
      } else {
        await api.updateOperaio(realId, { costoOrario: value });
      }
      setEditingRate(null); setNewRate('');
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleAddAcconto = async () => {
    if (!showAddAcconto || !accontoData.amount) return;
    try {
      await api.createAcconto({
        workerId: showAddAcconto.id,
        amount: parseFloat(accontoData.amount),
        note: accontoData.note,
        date: accontoData.date || new Date().toISOString().split('T')[0],
      });
      setShowAddAcconto(null);
      setAccontoData({ amount: '', note: '', date: '' });
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteAcconto = async (id) => {
    try { await api.deleteAcconto(id); loadData(); } catch (e) { console.error(e); }
  };

  const openEditAcconto = (a) => {
    setEditAccontoData({ amount: String(a.amount), note: a.note || '', date: a.date || '' });
    setEditingAcconto(a);
  };

  const handleSaveAcconto = async () => {
    if (!editingAcconto) return;
    try {
      await api.updateAcconto(editingAcconto.id, {
        amount: parseFloat(editAccontoData.amount),
        note: editAccontoData.note,
        date: editAccontoData.date,
      });
      setEditingAcconto(null);
      loadData();
    } catch (e) { console.error(e); }
  };

  // Timbrature editing in Registro Paga
  const getWorkerTimbrature = (workerId) => {
    return timbrature.filter(t => t.workerId === workerId)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const openEditTimb = (t) => {
    const dt = new Date(t.timestamp);
    setEditTimbData({
      date: dt.toISOString().split('T')[0],
      time: dt.toTimeString().slice(0, 5),
      type: t.type
    });
    setEditingTimb(t);
  };

  const handleSaveTimb = async () => {
    if (!editingTimb || !editTimbData.date || !editTimbData.time) return;
    try {
      await api.updateTimbratura(editingTimb.id, {
        timestamp: `${editTimbData.date}T${editTimbData.time}:00`,
        type: editTimbData.type
      });
      setEditingTimb(null);
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteTimb = async (id) => {
    try { await api.deleteTimbratura(id); loadData(); } catch (e) { console.error(e); }
  };

  const handleAddManualTimb = async () => {
    if (!addingTimb || !newTimbData.date || !newTimbData.time) return;
    try {
      // Create timbratura with specific timestamp via backend
      await api.createTimbratura({
        workerId: addingTimb.id,
        type: newTimbData.type,
        lat: 0, lng: 0
      });
      // Then update its timestamp to the manual time
      const all = await api.getTimbratureRange(newTimbData.date, newTimbData.date);
      const latest = all.find(t => t.workerId === addingTimb.id && t.type === newTimbData.type);
      if (latest) {
        await api.updateTimbratura(latest.id, {
          timestamp: `${newTimbData.date}T${newTimbData.time}:00`
        });
      }
      setAddingTimb(null);
      setNewTimbData({ date: '', time: '', type: 'ingresso' });
      loadData();
    } catch (e) { console.error(e); }
  };

  // Summary totals
  const totalPayable = operai.reduce((sum, w) => {
    const { totalHours } = calcWorkerData(w.id);
    return sum + (totalHours * (w.costoOrario || 0));
  }, 0);
  const totalAccontiPaid = acconti.reduce((sum, a) => sum + a.amount, 0);
  const totalBustePaga = operai.reduce((sum, w) => sum + (parseFloat(w.bustaPaga) || 0), 0);
  const totalBalance = totalPayable - totalAccontiPaid;
  const totalCash = totalBalance - totalBustePaga;

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Registro Paga" subtitle="Calcolo paghe, acconti e saldi" />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Registro Paga</h1>
          <p className="page-subtitle">Calcolo paghe, acconti e saldi per operaio</p>
        </div>
        <div className="header-right">
          <SectionActions sectionTitle="Registro Paga" contentRef={pageRef} />
        </div>
      </div>

      {/* Date Range */}
      <div className="paga-date-range">
        <div className="date-range-box">
          <Calendar size={16} />
          <span className="date-label">Periodo:</span>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="date-input" />
          <span className="date-sep">—</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="date-input" />
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Select value={selectedForPrint} onValueChange={setSelectedForPrint}>
            <SelectTrigger style={{ width: 220 }}>
              <SelectValue placeholder="Seleziona operaio..." />
            </SelectTrigger>
            <SelectContent>
              {operai.map(w => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button 
            className="big-action-btn" 
            style={{ background: '#a78bfa', borderColor: '#a78bfa', padding: '8px 16px', height: '40px', color: '#ffffff', fontWeight: 600 }}
            onClick={() => setShowPrintPreview(true)}
            disabled={!selectedForPrint}
          >
            <Receipt size={18} /> Stampa Busta Paga
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="paga-stats">
        <div className="paga-stat">
          <Euro size={22} color="#22c55e" />
          <span className="paga-stat-value">€{totalPayable.toFixed(2)}</span>
          <span className="paga-stat-label">Totale Da Pagare</span>
        </div>
        <div className="paga-stat">
          <Wallet size={22} color="#f59e0b" />
          <span className="paga-stat-value">€{totalAccontiPaid.toFixed(2)}</span>
          <span className="paga-stat-label">Acconti Versati</span>
        </div>
        <div className="paga-stat">
          <Receipt size={22} color="#8b5cf6" />
          <span className="paga-stat-value">€{totalBustePaga.toFixed(2)}</span>
          <span className="paga-stat-label">Buste Paga</span>
        </div>
        <div className="paga-stat">
          <TrendingUp size={22} color={totalBalance >= 0 ? '#22c55e' : '#ef4444'} />
          <span className="paga-stat-value" style={{ color: totalBalance >= 0 ? '#22c55e' : '#ef4444' }}>€{totalBalance.toFixed(2)}</span>
          <span className="paga-stat-label">Saldo Residuo</span>
        </div>
        <div className="paga-stat">
          <Wallet size={22} color="#10b981" />
          <span className="paga-stat-value" style={{ color: '#10b981' }}>€{totalCash.toFixed(2)}</span>
          <span className="paga-stat-label">Paga Cash</span>
        </div>
        <div className="paga-stat">
          <Users size={22} color="#3b82f6" />
          <span className="paga-stat-value">{operai.length}</span>
          <span className="paga-stat-label">Operai</span>
        </div>
      </div>

      {/* Workers Payroll List */}
      <div className="paga-list">
        {operai.map(worker => {
          const { totalHours, cantiereHours, dailyHours } = calcWorkerData(worker.id);
          const earned = totalHours * (worker.costoOrario || 0);
          const wAcconti = getWorkerAcconti(worker.id);
          const totalAdv = getTotalAcconti(worker.id);
          const bustaPaga = parseFloat(worker.bustaPaga) || 0;
          const balance = earned - totalAdv;
          const isExpanded = expandedWorker === worker.id;

          const cantiereChartData = Object.entries(cantiereHours).map(([name, hours]) => ({
            name: name.length > 20 ? name.slice(0, 20) + '...' : name,
            ore: parseFloat(hours.toFixed(1)),
            guadagno: parseFloat((hours * (worker.costoOrario || 0)).toFixed(2)),
          }));

          const dailyChartData = Object.entries(dailyHours)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([day, hours]) => ({
              giorno: new Date(day).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
              ore: parseFloat(hours.toFixed(1)),
            }));

          return (
            <div key={worker.id} className={`paga-card ${isExpanded ? 'paga-expanded' : ''}`}>
              {/* Summary Row */}
              <div className="paga-row" onClick={() => setExpandedWorker(isExpanded ? null : worker.id)}>
                <div className="paga-worker">
                  <div className="paga-avatar">{getInitials(worker.name)}</div>
                  <div>
                    <h4 className="paga-name">{worker.name}</h4>
                    <p className="paga-role">{worker.role} — {worker.badge}</p>
                  </div>
                </div>
                <div className="paga-cells">
                  <div className="paga-cell">
                    <span className="paga-cell-label">€/ora</span>
                    <span className="paga-cell-value paga-rate" onClick={(e) => { e.stopPropagation(); setEditingRate(worker.id); setNewRate(String(worker.costoOrario || '')); }}>
                      €{(worker.costoOrario || 0).toFixed(2)} <Edit size={11} />
                    </span>
                  </div>
                  <div className="paga-cell">
                    <span className="paga-cell-label">Giornate</span>
                    <span className="paga-cell-value">{(() => {
                      const giorni = totalHours / 8;
                      const intere = Math.floor(giorni);
                      const fraz = giorni - intere;
                      if (giorni === 0) return '-';
                      if (fraz < 0.1) return `${intere}`;
                      return `${intere},${Math.round(fraz * 10) >= 5 ? '5' : Math.round(fraz * 10)}`;
                    })()}</span>
                  </div>
                  <div className="paga-cell">
                    <span className="paga-cell-label">Ore</span>
                    <span className="paga-cell-value">{totalHours.toFixed(1)}h</span>
                  </div>
                  <div className="paga-cell">
                    <span className="paga-cell-label">Maturato</span>
                    <span className="paga-cell-value" style={{ color: '#22c55e' }}>€{earned.toFixed(2)}</span>
                  </div>
                  <div className="paga-cell">
                    <span className="paga-cell-label">Acconti</span>
                    <span className="paga-cell-value" style={{ color: '#f59e0b' }}>€{totalAdv.toFixed(2)}</span>
                  </div>
                  <div className="paga-cell">
                    <span className="paga-cell-label">Busta Paga</span>
                    <span className="paga-cell-value paga-rate" style={{ color: '#8b5cf6' }} onClick={(e) => { e.stopPropagation(); setEditingRate(worker.id + '_busta'); setNewRate(String(bustaPaga || '')); }}>
                      €{bustaPaga.toFixed(2)} <Edit size={11} />
                    </span>
                  </div>
                  <div className="paga-cell">
                    <span className="paga-cell-label">Saldo</span>
                    <span className="paga-cell-value paga-balance" style={{ color: balance >= 0 ? '#22c55e' : '#ef4444' }}>
                      €{balance.toFixed(2)}
                    </span>
                  </div>
                </div>
                <button className="paga-add-acconto" onClick={(e) => { e.stopPropagation(); setShowAddAcconto(worker); setAccontoData({ amount: '', note: '', date: '' }); }} title="Aggiungi Acconto">
                  <Plus size={14} /> Acconto
                </button>
                {isExpanded ? <ChevronUp size={18} className="paga-chevron" /> : <ChevronDown size={18} className="paga-chevron" />}
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="paga-detail">
                  <div className="paga-detail-grid">
                    {/* Hours Chart */}
                    {dailyChartData.length > 0 && (
                      <div className="paga-chart-card">
                        <h5>Ore Lavorate per Giorno</h5>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={dailyChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="giorno" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                            <Tooltip />
                            <Bar dataKey="ore" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Ore" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Cantiere Chart */}
                    {cantiereChartData.length > 0 && (
                      <div className="paga-chart-card">
                        <h5>Ore e Guadagni per Cantiere</h5>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={cantiereChartData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#9ca3af' }} width={120} />
                            <Tooltip />
                            <Bar dataKey="ore" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Ore" />
                            <Bar dataKey="guadagno" fill="#22c55e" radius={[0, 4, 4, 0]} name="€ Guadagno" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* Cantieri Lavorati - Collapsable */}
                  <div className="paga-cantieri-worked">
                    <div className="paga-acconti-header" style={{ cursor: 'pointer', marginBottom: 8 }} onClick={() => setExpandedCantieri(expandedCantieri === worker.id ? null : worker.id)}>
                      <h5>Cantieri Dove Ha Lavorato ({Object.keys(cantiereHours).length})</h5>
                      {expandedCantieri === worker.id ? <ChevronUp size={18} color="#64748B" /> : <ChevronDown size={18} color="#64748B" />}
                    </div>
                    {expandedCantieri === worker.id && (
                      <div className="paga-cantieri-list">
                        {Object.entries(cantiereHours).map(([name, hours], i) => (
                          <div key={i} className="paga-cantiere-row">
                            <Building2 size={14} color={COLORS[i % COLORS.length]} />
                            <span className="pcr-name">{name}</span>
                            <span className="pcr-hours">{hours.toFixed(1)}h</span>
                            <span className="pcr-amount">€{(hours * (worker.costoOrario || 0)).toFixed(2)}</span>
                          </div>
                        ))}
                        {Object.keys(cantiereHours).length === 0 && <p className="empty-text">Nessuna ora registrata nel periodo</p>}
                      </div>
                    )}
                  </div>

                  {/* Timbrature List - Collapsable */}
                  <div className="paga-timbrature-section">
                    <div className="paga-acconti-header" style={{ cursor: 'pointer' }} onClick={() => setExpandedTimbrature(expandedTimbrature === worker.id ? null : worker.id)}>
                      <h5>Timbrature nel Periodo ({getWorkerTimbrature(worker.id).length})</h5>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button className="paga-add-acconto-sm" style={{ color: '#3b82f6', borderColor: '#93c5fd' }} onClick={(e) => { e.stopPropagation(); setAddingTimb(worker); setNewTimbData({ date: startDate, time: '08:00', type: 'ingresso' }); }}>
                          <Plus size={14} /> Aggiungi Timbratura
                        </button>
                        {expandedTimbrature === worker.id ? <ChevronUp size={18} color="#64748B" /> : <ChevronDown size={18} color="#64748B" />}
                      </div>
                    </div>
                    {expandedTimbrature === worker.id && (
                      <div className="paga-timb-list">
                        {getWorkerTimbrature(worker.id).map(t => (
                          <div key={t.id} className="paga-timb-row">
                            <div className={`paga-timb-icon ${t.type === 'ingresso' ? 'type-in' : 'type-out'}`}>
                              {t.type === 'ingresso' ? <LogIn size={13} /> : <LogOut size={13} />}
                            </div>
                            <span className="pt-type">{t.type === 'ingresso' ? 'Ingresso' : 'Uscita'}</span>
                            <span className="pt-date">{new Date(t.timestamp).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' })}</span>
                            <span className="pt-time">{new Date(t.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                            <span className="pt-cantiere">{t.cantiereName}</span>
                            <button className="pa-edit" onClick={() => openEditTimb(t)} title="Modifica"><Edit size={13} /></button>
                            <button className="pa-delete" onClick={() => handleDeleteTimb(t.id)} title="Elimina"><Trash2 size={13} /></button>
                          </div>
                        ))}
                        {getWorkerTimbrature(worker.id).length === 0 && <p className="empty-text">Nessuna timbratura nel periodo</p>}
                      </div>
                    )}
                  </div>

                  {/* Acconti List */}
                  <div className="paga-acconti-section">
                    <div className="paga-acconti-header">
                      <h5>Acconti Ricevuti ({wAcconti.length})</h5>
                      <button className="paga-add-acconto-sm" onClick={() => { setShowAddAcconto(worker); setAccontoData({ amount: '', note: '', date: '' }); }}>
                        <Plus size={14} /> Nuovo Acconto
                      </button>
                    </div>
                    <div className="paga-acconti-list">
                      {wAcconti.map(a => (
                        <div key={a.id} className="paga-acconto-row">
                          <Receipt size={14} color="#f59e0b" />
                          <span className="pa-date">{new Date(a.date).toLocaleDateString('it-IT')}</span>
                          <span className="pa-amount">€{a.amount.toFixed(2)}</span>
                          <span className="pa-note">{a.note || '-'}</span>
                          <button className="pa-edit" onClick={() => openEditAcconto(a)} title="Modifica acconto"><Edit size={13} /></button>
                          <button className="pa-delete" onClick={() => handleDeleteAcconto(a.id)} title="Elimina"><Trash2 size={13} /></button>
                        </div>
                      ))}
                      {wAcconti.length === 0 && <p className="empty-text">Nessun acconto registrato</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Rate Dialog */}
      {editingRate && (
        <Dialog open={!!editingRate} onOpenChange={() => setEditingRate(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingRate.includes('_busta') ? 'Modifica Busta Paga' : 'Modifica Costo Orario'}</DialogTitle>
            </DialogHeader>
            <div className="form-fields">
              <div className="form-field">
                <Label>{editingRate.includes('_busta') ? 'Importo Busta Paga (€)' : 'Costo Orario (€/ora)'}</Label>
                <Input type="number" step="0.01" value={newRate} onChange={e => setNewRate(e.target.value)} placeholder={editingRate.includes('_busta') ? 'Es: 600.00' : 'Es: 12.50'} />
                {editingRate.includes('_busta') && <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Lascia a 0 se non è stata pagata busta</p>}
              </div>
              <button className="form-submit" onClick={() => handleSaveRate(editingRate)}>Salva</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Acconto Dialog */}
      {showAddAcconto && (
        <Dialog open={!!showAddAcconto} onOpenChange={() => setShowAddAcconto(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo Acconto — {showAddAcconto.name}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field">
                <Label>Importo (€) *</Label>
                <Input type="number" step="0.01" value={accontoData.amount} onChange={e => setAccontoData({...accontoData, amount: e.target.value})} placeholder="Es: 500.00" />
              </div>
              <div className="form-field">
                <Label>Data</Label>
                <Input type="date" value={accontoData.date} onChange={e => setAccontoData({...accontoData, date: e.target.value})} />
              </div>
              <div className="form-field">
                <Label>Note</Label>
                <Input value={accontoData.note} onChange={e => setAccontoData({...accontoData, note: e.target.value})} placeholder="Es: Acconto mensile luglio" />
              </div>
              <button className="form-submit" onClick={handleAddAcconto} disabled={!accontoData.amount}>Registra Acconto</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Acconto Dialog */}
      {editingAcconto && (
        <Dialog open={!!editingAcconto} onOpenChange={() => setEditingAcconto(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifica Acconto</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Importo (€)</Label><Input type="number" step="0.01" value={editAccontoData.amount} onChange={e => setEditAccontoData({...editAccontoData, amount: e.target.value})} /></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={editAccontoData.date} onChange={e => setEditAccontoData({...editAccontoData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Note</Label><Input value={editAccontoData.note} onChange={e => setEditAccontoData({...editAccontoData, note: e.target.value})} /></div>
              <button className="form-submit" onClick={handleSaveAcconto}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Timbratura Dialog */}
      {editingTimb && (
        <Dialog open={!!editingTimb} onOpenChange={() => setEditingTimb(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifica Timbratura — {editingTimb.workerName}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Tipo</Label>
                <Select value={editTimbData.type} onValueChange={v => setEditTimbData({...editTimbData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ingresso">Ingresso</SelectItem><SelectItem value="uscita">Uscita</SelectItem></SelectContent>
                </Select></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={editTimbData.date} onChange={e => setEditTimbData({...editTimbData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Ora</Label><Input type="time" value={editTimbData.time} onChange={e => setEditTimbData({...editTimbData, time: e.target.value})} /></div>
              <button className="form-submit" onClick={handleSaveTimb}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Manual Timbratura Dialog */}
      {addingTimb && (
        <Dialog open={!!addingTimb} onOpenChange={() => setAddingTimb(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Aggiungi Timbratura Manuale — {addingTimb.name}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Tipo *</Label>
                <Select value={newTimbData.type} onValueChange={v => setNewTimbData({...newTimbData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ingresso">Ingresso</SelectItem><SelectItem value="uscita">Uscita</SelectItem></SelectContent>
                </Select></div>
              <div className="form-field"><Label>Data *</Label><Input type="date" value={newTimbData.date} onChange={e => setNewTimbData({...newTimbData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Ora *</Label><Input type="time" value={newTimbData.time} onChange={e => setNewTimbData({...newTimbData, time: e.target.value})} /></div>
              <button className="form-submit" onClick={handleAddManualTimb} disabled={!newTimbData.date || !newTimbData.time}>Aggiungi Timbratura</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Print Busta Paga Dialog */}
      {showPrintPreview && selectedForPrint && (() => {
        const worker = operai.find(w => w.id === selectedForPrint);
        if (!worker) return null;
        const { totalHours, cantiereHours } = calcWorkerData(worker.id);
        const earned = totalHours * (worker.costoOrario || 0);
        const totalAdv = getTotalAcconti(worker.id);
        const bustaPaga = parseFloat(worker.bustaPaga) || 0;
        const balance = earned - totalAdv;
        const wAcconti = getWorkerAcconti(worker.id);
        
        return (
          <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
            <DialogContent style={{ maxWidth: '900px', maxHeight: '95vh', overflowY: 'auto' }}>
              <div id="printable-busta-paga" style={{ padding: '40px', background: '#fff' }}>
                {/* Header */}
                <div style={{ borderBottom: '3px solid #2563EB', paddingBottom: 20, marginBottom: 30 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h1 style={{ fontSize: 36, fontWeight: 800, color: '#0F172A', margin: 0 }}>COLABELLA GROUP</h1>
                      <p style={{ fontSize: 16, color: '#64748B', margin: '6px 0 0' }}>Busta Paga - Riepilogo Mensile</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: 14, color: '#64748B', margin: 0 }}>Data Emissione</p>
                      <p style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', margin: '4px 0 0' }}>{new Date().toLocaleDateString('it-IT')}</p>
                    </div>
                  </div>
                </div>

                {/* Worker Info */}
                <div style={{ background: '#F8FAFC', padding: 24, borderRadius: 12, marginBottom: 30, border: '1px solid #E2E8F0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    <div>
                      <p style={{ fontSize: 13, color: '#64748B', margin: 0, textTransform: 'uppercase', fontWeight: 600 }}>Operaio</p>
                      <p style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', margin: '6px 0 0' }}>{worker.name}</p>
                      <p style={{ fontSize: 15, color: '#64748B', margin: '4px 0 0' }}>{worker.role} — {worker.badge}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: 13, color: '#64748B', margin: 0, textTransform: 'uppercase', fontWeight: 600 }}>Periodo di Riferimento</p>
                      <p style={{ fontSize: 17, fontWeight: 600, color: '#0F172A', margin: '6px 0 0' }}>
                        {new Date(startDate).toLocaleDateString('it-IT')} — {new Date(endDate).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dettaglio Ore per Cantiere */}
                <div style={{ marginBottom: 30 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', marginBottom: 16 }}>Dettaglio Ore Lavorate</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #E2E8F0' }}>
                    <thead>
                      <tr style={{ background: '#F8FAFC' }}>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #E2E8F0', fontSize: 14, fontWeight: 700, color: '#64748B' }}>CANTIERE</th>
                        <th style={{ padding: '14px', textAlign: 'center', borderBottom: '2px solid #E2E8F0', fontSize: 14, fontWeight: 700, color: '#64748B' }}>ORE</th>
                        <th style={{ padding: '14px', textAlign: 'right', borderBottom: '2px solid #E2E8F0', fontSize: 14, fontWeight: 700, color: '#64748B' }}>IMPORTO</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(cantiereHours).map(([name, hours], i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          <td style={{ padding: '12px 14px', fontSize: 15, color: '#0F172A' }}>{name}</td>
                          <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 15, fontWeight: 600, color: '#3b82f6' }}>{hours.toFixed(1)}h</td>
                          <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: 15, fontWeight: 600, color: '#22c55e' }}>€{(hours * (worker.costoOrario || 0)).toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: '#F8FAFC', fontWeight: 700 }}>
                        <td style={{ padding: '14px', fontSize: 16, color: '#0F172A' }}>TOTALE ORE LAVORATE</td>
                        <td style={{ padding: '14px', textAlign: 'center', fontSize: 17, color: '#2563EB' }}>{totalHours.toFixed(1)}h</td>
                        <td style={{ padding: '14px', textAlign: 'right', fontSize: 17, color: '#0F172A' }}>€{earned.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Acconti */}
                {wAcconti.length > 0 && (
                  <div style={{ marginBottom: 30 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', marginBottom: 16 }}>Acconti Ricevuti</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #E2E8F0' }}>
                      <thead>
                        <tr style={{ background: '#F8FAFC' }}>
                          <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #E2E8F0', fontSize: 14, fontWeight: 700, color: '#64748B' }}>DATA</th>
                          <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #E2E8F0', fontSize: 14, fontWeight: 700, color: '#64748B' }}>NOTE</th>
                          <th style={{ padding: '14px', textAlign: 'right', borderBottom: '2px solid #E2E8F0', fontSize: 14, fontWeight: 700, color: '#64748B' }}>IMPORTO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wAcconti.map((a, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                            <td style={{ padding: '12px 14px', fontSize: 15, color: '#0F172A' }}>{new Date(a.date).toLocaleDateString('it-IT')}</td>
                            <td style={{ padding: '12px 14px', fontSize: 14, color: '#64748B' }}>{a.note || '-'}</td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: 15, fontWeight: 600, color: '#f59e0b' }}>€{a.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Riepilogo Finale */}
                <div style={{ background: '#F8FAFC', padding: 28, borderRadius: 12, border: '2px solid #E2E8F0' }}>
                  <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', marginBottom: 20, textAlign: 'center' }}>RIEPILOGO PAGAMENTO</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #E2E8F0' }}>
                      <span style={{ fontSize: 16, color: '#64748B' }}>Tariffa Oraria:</span>
                      <span style={{ fontSize: 17, fontWeight: 600, color: '#0F172A' }}>€{(worker.costoOrario || 0).toFixed(2)}/h</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #E2E8F0' }}>
                      <span style={{ fontSize: 16, color: '#64748B' }}>Totale Ore Lavorate:</span>
                      <span style={{ fontSize: 17, fontWeight: 600, color: '#3b82f6' }}>{totalHours.toFixed(1)}h</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '2px solid #CBD5E1' }}>
                      <span style={{ fontSize: 17, fontWeight: 600, color: '#0F172A' }}>Totale Maturato:</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: '#22c55e' }}>€{earned.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
                      <span style={{ fontSize: 16, color: '#64748B' }}>- Acconti Ricevuti:</span>
                      <span style={{ fontSize: 17, fontWeight: 600, color: '#f59e0b' }}>€{totalAdv.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '2px solid #CBD5E1' }}>
                      <span style={{ fontSize: 16, color: '#64748B' }}>- Busta Paga:</span>
                      <span style={{ fontSize: 17, fontWeight: 600, color: '#8b5cf6' }}>€{bustaPaga.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', background: balance >= 0 ? '#ecfdf5' : '#fef2f2', margin: '12px -28px -28px', padding: '20px 28px', borderRadius: '0 0 12px 12px' }}>
                      <span style={{ fontSize: 19, fontWeight: 700, color: '#0F172A' }}>SALDO FINALE:</span>
                      <span style={{ fontSize: 26, fontWeight: 800, color: balance >= 0 ? '#22c55e' : '#ef4444' }}>€{balance.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                  <div>
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>Colabella Group S.r.l.</p>
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: '2px 0 0' }}>Documento generato il {new Date().toLocaleDateString('it-IT')}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 8px' }}>Firma per Ricevuta</p>
                    <div style={{ borderBottom: '1px solid #CBD5E1', width: 200, height: 40 }}></div>
                  </div>
                </div>
              </div>

              {/* Print Button */}
              <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="form-cancel" onClick={() => setShowPrintPreview(false)}>Chiudi</button>
                <button 
                  className="form-submit" 
                  style={{ background: '#8b5cf6' }}
                  onClick={() => {
                    const printContent = document.getElementById('printable-busta-paga');
                    const winPrint = window.open('', '', 'width=900,height=650');
                    winPrint.document.write('<html><head><title>Busta Paga - ' + worker.name + '</title>');
                    winPrint.document.write('<style>body{font-family:Arial,sans-serif;margin:0;padding:20px;}@media print{body{margin:0;}}</style>');
                    winPrint.document.write('</head><body>');
                    winPrint.document.write(printContent.innerHTML);
                    winPrint.document.write('</body></html>');
                    winPrint.document.close();
                    winPrint.focus();
                    winPrint.print();
                  }}
                >
                  <Receipt size={16} /> Stampa
                </button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
};

export default RegistroPaga;
